import { Skeleton } from 'antd';

/**
 * Owner Analytics tab skeleton — Hero revenue card + KPI grid (2x2) +
 * chart area placeholder + top list placeholder + provider list +
 * stacked bar chart placeholder + 2-col stat cards.
 * No scroll container — parent TabContainer provides padding and overflow.
 */
export default function OwnerAnalyticsSkeleton() {
  return (
    <div className="bl-animate-in">
      {/* Hero revenue card */}
      <div
        style={{
          borderRadius: 18,
          background: 'var(--bl-surface-soft, #f0f2f5)',
          padding: '16px 16px 18px',
          marginBottom: 12,
        }}
      >
        <Skeleton.Input active size="small" style={{ width: 120, height: 11, marginBottom: 8 }} />
        <Skeleton.Input active size="small" style={{ width: 200, height: 26, marginBottom: 10 }} />
        <Skeleton.Input active size="small" style={{ width: 160, height: 14 }} />
      </div>

      {/* KPI grid — 2×2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              borderRadius: 14,
              background: 'var(--bl-surface-soft, #f7f8fa)',
              padding: '12px 14px',
              minHeight: 92,
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <Skeleton.Avatar active size={32} shape="square" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Skeleton.Input active size="small" style={{ width: 60, height: 16 }} />
              <Skeleton.Input active size="small" style={{ width: 80, height: 10 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Chart area placeholder (revenue trend) */}
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px',
          marginBottom: 12,
          minHeight: 180,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Skeleton.Input active size="small" style={{ width: 140, height: 14 }} />
        <Skeleton.Input
          active
          size="small"
          style={{ width: '100%', height: 110, borderRadius: 10 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton.Input key={i} active size="small" style={{ width: 28, height: 10 }} />
          ))}
        </div>
      </div>

      {/* Provider list placeholder — 3 rows */}
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px',
          marginBottom: 12,
        }}
      >
        <Skeleton.Input
          active
          size="small"
          style={{ width: 110, height: 14, marginBottom: 12 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <Skeleton.Avatar active size={20} shape="square" style={{ borderRadius: 6 }} />
                  <Skeleton.Input active size="small" style={{ width: '55%', height: 13 }} />
                </div>
                <Skeleton.Input active size="small" style={{ width: 56, height: 13 }} />
              </div>
              <Skeleton.Input active size="small" style={{ width: '100%', height: 6, borderRadius: 4 }} />
            </div>
          ))}
        </div>
      </div>

      {/* Stacked bar chart placeholder (revenue share / weekday load) */}
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px',
          marginBottom: 12,
        }}
      >
        <Skeleton.Input
          active
          size="small"
          style={{ width: 130, height: 14, marginBottom: 14 }}
        />
        <Skeleton.Input
          active
          size="small"
          style={{ width: '100%', height: 14, borderRadius: 8, marginBottom: 14 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Skeleton.Avatar active size={9} shape="circle" />
              <Skeleton.Input active size="small" style={{ flex: 1, height: 11 }} />
              <Skeleton.Input active size="small" style={{ width: 36, height: 11 }} />
            </div>
          ))}
        </div>
      </div>

      {/* 2-col stat cards placeholder (customers breakdown / payment) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[0, 1].map((i) => (
          <div
            key={i}
            style={{
              borderRadius: 12,
              background: 'var(--bl-surface-soft, #f7f8fa)',
              padding: '12px 14px',
              minHeight: 78,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <Skeleton.Input active size="small" style={{ width: 52, height: 22 }} />
            <Skeleton.Input active size="small" style={{ width: 64, height: 12 }} />
            <Skeleton.Input active size="small" style={{ width: 50, height: 10 }} />
          </div>
        ))}
      </div>

      {/* Top list placeholder — 4 rows */}
      <div
        style={{
          borderRadius: 16,
          background: 'var(--bl-surface, #fff)',
          boxShadow: 'var(--bl-shadow-soft, 0 1px 6px rgba(0,0,0,0.07))',
          padding: '14px 14px',
          marginBottom: 12,
        }}
      >
        <Skeleton.Input
          active
          size="small"
          style={{ width: 120, height: 14, marginBottom: 12 }}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <Skeleton.Avatar active size={36} shape="square" style={{ borderRadius: 10 }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Skeleton.Input active size="small" style={{ width: '65%', height: 13 }} />
                <Skeleton.Input active size="small" style={{ width: '45%', height: 10 }} />
              </div>
              <Skeleton.Input active size="small" style={{ width: 50, height: 14 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
